import { useTranslation } from "react-i18next";

export interface ProductFactsProps {
  category: string | undefined;
  presentation: string | undefined;
  stock: number | undefined;
}

interface Fact {
  label: string;
  value: string;
}

/**
 * La ficha técnica: categoría, presentación y existencias.
 *
 * ── Es un `<dl>` ─────────────────────────────────────────────────────
 * Cada fila es literalmente un término y su definición, y esa semántica
 * es la que hace que un lector lea "Presentación: frasco de 60 cápsulas"
 * en vez de dos textos sueltos que hay que emparejar de memoria.
 *
 * ── Un dato que no existe no ocupa una fila ──────────────────────────
 * Las tres son opcionales en el contrato. Una fila con un guion es ruido
 * que hay que leer para descubrir que no dice nada; si no queda ninguna,
 * la sección entera desaparece.
 *
 * ── Las existencias se dicen en número ───────────────────────────────
 * El badge de arriba comunica el ESTADO ("Agotado", "Solo quedan 3");
 * aquí va la cifra, que es lo que alguien mira cuando quiere pedir doce.
 * `0` es un valor legítimo y por eso la comprobación es contra
 * `undefined`, no contra el valor: un `if (stock)` escondería el agotado,
 * que es justo el dato que más importa.
 */
export function ProductFacts({ category, presentation, stock }: ProductFactsProps) {
  const { t } = useTranslation();

  const facts: Fact[] = [
    ...(category ? [{ label: t("Categoría"), value: category }] : []),
    ...(presentation ? [{ label: t("Presentación"), value: presentation }] : []),
    ...(stock !== undefined
      ? [{ label: t("Existencias"), value: t("{{n}} disponibles", { n: stock }) }]
      : []),
  ];

  if (facts.length === 0) return null;

  return (
    <dl className="grid divide-y divide-ds-border rounded-[22px] border border-ds-border bg-white/80">
      {facts.map((fact) => (
        <div key={fact.label} className="flex items-baseline gap-3 px-4 py-3">
          <dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-ds-ink-3">
            {fact.label}
          </dt>
          {/* `min-w-0` + `break-words`: una presentación larga se parte en
              vez de desbordar la caja por la derecha en móvil. */}
          <dd className="min-w-0 flex-1 break-words text-[13px] font-semibold text-ds-ink">
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
